"use client";

import { readFragment } from "@opensea/graphql";
import { Button } from "@opensea/next-ui-kit";
import { CopyToClipboard, Flex, FlexCenter, Modal, ModalBody, ModalHeader, ModalHeaderTitle } from "@opensea/ui-kit";
import { ArrowDownward, ContentCopy, Farcaster, Telegram, Twitter } from "@opensea/ui-kit/icons";
import { SITE_URL } from "@opensea/utils";
import { useTranslations } from "next-intl";
import { fetchFileFromUrl } from "../../../lib/upload";
import { getCollectionUrlBySlug } from "../urls";
import { CollectionShareModalFragment } from "./CollectionShareModal.fragment";
export function CollectionShareModal({
  collection,
  open,
  onOpenChange
}) {
  const t = useTranslations("CollectionShareModal");
  const collectionData = readFragment(CollectionShareModalFragment, collection);
  const collectionUrl = `${SITE_URL.origin}${getCollectionUrlBySlug(collectionData.slug)}`;
  const shareText = t("shareText", {
    collectionName: collectionData.name
  });
  const ogImageUrl = `${SITE_URL.origin}${getCollectionUrlBySlug(collectionData.slug)}/opengraph-image`;
  const socialShareUrls = {
    twitter: `https://x.com/intent/tweet?url=${encodeURIComponent(collectionUrl)}&text=${encodeURIComponent(shareText)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(collectionUrl)}&text=${encodeURIComponent(shareText)}`,
    farcaster: `https://warpcast.com/~/compose?text=${encodeURIComponent(`${shareText} ${collectionUrl}`)}`
  };
  const handleSocialShare = platform => {
    try {
      const newWindow = window.open(socialShareUrls[platform], "_blank", "noopener,noreferrer");
      if (!newWindow) {
        navigator.clipboard?.writeText(collectionUrl);
      }
    } catch (error) {
      console.error("Failed to open share dialog:", error);
      navigator.clipboard?.writeText(collectionUrl);
    }
  };
  const handleSaveImage = async () => {
    try {
      const file = await fetchFileFromUrl(ogImageUrl, `${collectionData.slug}-collection.png`);
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error_0) {
      console.error("Failed to save image:", error_0);
    }
  };
  const handleCopyImage = async () => {
    try {
      const response = await fetch(ogImageUrl);
      const blob = await response.blob();
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new ClipboardItem({
          [blob.type]: blob
        })]);
      } else {
        navigator.clipboard?.writeText(ogImageUrl);
      }
    } catch (error_1) {
      console.error("Failed to copy image:", error_1);
      navigator.clipboard?.writeText(ogImageUrl);
    }
  };
  return <Modal content={<>
          <ModalHeader>
            <ModalHeaderTitle>{t("title")}</ModalHeaderTitle>
          </ModalHeader>

          <ModalBody className="space-y-6">
            {collectionData.imageUrl && <FlexCenter>
                <FlexCenter className="h-[250px] w-full max-w-lg overflow-hidden rounded-lg border border-border-1 bg-bg-additional-1">
                  <img alt={t("ogImageAlt", {
            collectionName: collectionData.name
          })} className="h-full w-full rounded-lg object-cover" onError={e => {
            const target = e.target;
            if (collectionData.imageUrl) {
              target.src = collectionData.imageUrl;
            }
          }} src={ogImageUrl} />
                </FlexCenter>
              </FlexCenter>}

            <div className="space-y-3">
              <Button className="!bg-black !text-white hover:!bg-gray-800 !border-black w-full" icon={Twitter} onClick={() => handleSocialShare("twitter")} variant="secondary">
                {t("shareOnX")}
              </Button>

              <Button className="w-full" icon={Telegram} onClick={() => handleSocialShare("telegram")} variant="secondary">
                {t("shareOnTelegram")}
              </Button>

              <Button className="w-full" icon={Farcaster} onClick={() => handleSocialShare("farcaster")} variant="secondary">
                {t("shareOnFarcaster")}
              </Button>

              <CopyToClipboard fullWidth text={collectionUrl} tooltipText={t("copyLinkTooltip")}>
                <Button className="w-full" icon={ContentCopy} variant="secondary">
                  {t("copyLink")}
                </Button>
              </CopyToClipboard>

              <Flex className="gap-2">
                <Button className="flex-1" icon={ContentCopy} onClick={handleCopyImage} variant="secondary">
                  {t("copyImage")}
                </Button>

                <Button className="flex-1" icon={ArrowDownward} onClick={handleSaveImage} variant="secondary">
                  {t("saveImage")}
                </Button>
              </Flex>
            </div>
          </ModalBody>
        </>} onOpenChange={onOpenChange} open={open} size="lg" />;
}